<template>
  <div>
    <div v-if="firstMessage" class="first-message">
      <div v-if="sender && !system" class="chat-message" :class="[sender]">
        <div class="avatar">
          <img v-if="avatar" class="avatar-img" :class="{glow: userOpts.privileges[privileges.CHAT_GLOW] && userOpts.showChatGlow}" :style="{color: userOpts.chatGlowColor || 'gold' }" :src="avatar" :title="isFromPatron ? '👑 Patron' : ''"/>
          <SolidSpinner v-else-if="avatar === undefined" class="is-primary" size="16px"/>
        </div>
        <div class="chat-message-div" :class="{patron: isFromPatron}">
          <div>
            <div class="from-container">
              <p class="from truncate">
                <Crown class="crown" v-if="userOpts.privileges[privileges.CHAT_CROWN] && userOpts.showChatCrown"/>
                {{from}}
              </p>
              <slot name="from-slot"></slot>
            </div>
            <!-- allowPin is about privileges, showPin is about logic of when and where it should be shown -->
            <ChatMessageEntry
                v-for="(msgID, $index) in filteredMessages"
                :key="$index"
                :uuid="msgID"
                :msg="rawMessages[msgID].msg"
                :editing="editing === msgID"
                :edited="!!rawMessages[msgID].edited"
                @update:editing="val => onUpdateEditing(msgID, val)"
                :safe-message="safeMessage"
                :allow-pin="allowPin"
                :allow-unpin="allowUnpin"
                :allow-edit="allowEdit"
                :allow-delete="allowDelete"
                :show-pin="showPin && !pinnedMessages[msgID]"
                :show-unpin="showUnpin && pinnedMessages[msgID]"
                :show-edit="showEdit"
                :show-delete="showDelete"
                :ref="msgID"
                @edit="$event => $emit('edit', $event)"
                @delete="handleDelete"
                @pin="handlePin"
                :extensions="[showdownXSSFilter, atAdminExt]"/>
          </div>
          <time v-if="time" class="right" :class="{ 'tooltip is-tooltip-top': !noTooltip }" :data-tooltip="time.format('MMM DD, YYYY @ hh:mm:ss A')">{{ time.format('hh:mm A')}}</time>
        </div>
      </div>
      <div v-else-if="system" class="system-message chat-message tooltip is-tooltip-top" :data-tooltip="time.format('MMM DD, YYYY @ hh:mm:ss A')"
          style="padding-top: 0; padding-bottom: 0;">
        <div class="system-message-div" style="flex: 1;">
          <span class="text-content">
            <i-mdi-information class="system-notice-icon" width="18" height="18"/>
            <VueShowdown class="text">
              {{ safeMessage(rawMessages[messages[0].uuid].msg) }}
            </VueShowdown>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import ChatMessageEntry from '@RC/communications/chat-message-entry.vue'
import { privileges } from '@root/backend/patron-privileges/privileges'

import xss from 'xss'

const xssFilter = new xss.FilterXSS({
  whiteList: {
    div: ['class', 'style'],
    a: ['href', 'title', 'target'],
    p: ['class', 'style'],
    span: ['class', 'style'],
    strong: ['class', 'style'],
    em: ['class', 'style'],
    h1: ['class', 'style'],
    h2: ['class', 'style'],
    h3: ['class', 'style'],
    h4: ['class', 'style'],
    h5: ['class', 'style'],
    h6: ['class', 'style'],
    b: ['class', 'style'],
    i: ['class', 'style'],
    ul: ['class', 'style'],
    ol: ['class', 'style'],
    li: ['class', 'style'],
    pre: ['class', 'style'],
    code: ['class', 'style'],
    kbd: ['class', 'style'],
    br: ['class', 'style'],
    hr: ['class', 'style'],
    blockquote: ['class', 'style'],
    del: ['class', 'style']
  }
})

export default {
  name: 'chat-message',
  components: {
    ChatMessageEntry,
    Crown: () => import(/* webpackChunkName: "crown-2" */ '@C/crown.vue'),
    SolidSpinner: () => import(/* webpackChunkName: "solid-spinner" */ '@C/common/solid-spinner.vue'),
    VueShowdown: () => import(/* webpackChunkName: "vue-showdown" */ 'vue-showdown/src/components/vue-showdown')
  },
  props: ['messages', 'time', 'rawMessages', 'pinnedMessages', 'roomUsers', 'allowPin', 'allowUnpin', 'allowEdit', 'allowDelete', 'showPin', 'showUnpin', 'showEdit', 'showDelete', 'editing', 'noTooltip'],
  computed: {
    privileges () {
      return privileges
    },
    firstMessage () {
      return this.rawMessages[this.messages[0].uuid]
    },
    nickname () {
      return this.roomUsers[this.userHash].nickname
    },
    userHash () {
      return this.firstMessage.userHash
    },
    sender () {
      return this.firstMessage.sender
    },
    system () {
      return this.firstMessage.system
    },
    from () {
      try {
        return decodeURIComponent(this.nickname)
        // In case it contains 'hash, etc'
      } catch (e) {
        return this.nickname
      }
    },
    avatar () {
      return this.userOpts && this.userOpts.avatar
    },
    filteredMessages () {
      return this.messages.filter(({ uuid }) => this.rawMessages[uuid]).map(x => x.uuid)
    },
    isFromPatron () {
      return this.userOpts.isPatron
    },
    userOpts () {
      return this.roomUsers[this.userHash]
    }
  },
  watch: {
  },
  data () {
    return {
      atAdminExt: () => [{
        type: 'lang',
        filter (text, converter, options) {
          return text
        }
      }],
      showdownXSSFilter: () => {
        return [{
          type: 'output',
          filter (text, converter, options) {
            const filtered = xssFilter.process(text)
            return filtered
          }
        }]
      }
    }
  },
  methods: {
    safeMessage (msg, edited) {
      let text = ''
      if (typeof msg !== 'string') {
        text = msg.msg
        if (msg.extra) {
          text += `
            <div class="extra">
              <span>
                ${msg.extra}
              </span>
            </div>
          `
        }
      } else {
        text = msg
      }
      let result
      try {
        result = decodeURIComponent(text)
        if (edited) {
          result += '<span style="font-size: 10.5px !important; margin-left: 4px; color: grey; line-height: inherit;">(edited)</span>'
        }
      } catch (e) {
        result = text
      }
      return result
    },
    formatTime (t) {
      const localTime = moment.utc(t).local()
      const timeStr = localTime.format('MM/DD HH:mm')
      return timeStr
    },
    async handleEdit (msg, uuid) {
      await this.$emit('edit', { uuid, msg })
    },
    async handlePin (e) {
      await this.$emit('pin', e)
    },
    async handleDelete (e) {
      await this.$emit('delete', e)
    },
    async triggerEdit ({ uuid }) {
    },
    onUpdateEditing (msgID, val) {
      if (val) {
        val = msgID
      }
      this.$emit('update:editing', val)
    }
  },
  beforeMount () {
  },
  mounted () {
  }
}
</script>

<style lang="scss" scoped>
$glowDuration: 2000ms;
$maxGlowShadow: 4px;

@mixin glow($animation-name) {
  @include keyframes($animation-name) {
    from { box-shadow: 0 0 0px; }
    to { box-shadow: 0 0 #{$maxGlowShadow}; }
  }
}

@include glow(glowing);
@include glow(dark-glowing);

.glow {
  animation: glowing #{$glowDuration} infinite alternate;
  animation-timing-function: ease-in-out;
}

.dark {
  .glow {
    animation: dark-glowing #{$glowDuration} infinite alternate;
    animation-timing-function: ease-in-out;
  }
  .from {
    .crown {
      color: gold;
    }
  }
}

.from {
  .crown {
    top: -3px;
    left: -3px;
    width: 14px;
    height: 14px;
    z-index: 1;
    transform: rotate(-35deg);
    color: darken(gold, 10);
  }
}
</style>

<style lang="scss">
$chat-system-margin-lr: 5px;

.dark, .theater-mode {
  .self .avatar::after {
    border-left-color: $chat-dark-msg-bg;
    border-bottom-color: $chat-dark-msg-bg;
    // box-shadow: none;
  }
  .other .avatar::after {
    border-top-color: $chat-dark-msg-bg;
    border-right-color: $chat-dark-msg-bg;
  }

  .chat-message-div, .system-message-div {
    background: $chat-dark-msg-bg;
    color: $chat-dark-text;
    hr {
      border-bottom-color: $chat-dark-system-hr-color;
    }
    time {
      color: darken($chat-dark-text, 15);
    }
  }
  .system-message-div {
    background: inherit;
    color: $chat-dark-system-notice-color !important;
    .system-notice-icon {
      color: $chat-dark-system-notice-icon-color;
    }
    .text-content {
      &::before, &::after {
        background-color: $chat-dark-system-hr-color;
      }
    }
  }
}

.avatar {
  flex-shrink: 0;
  img {
    width: 32px;
    height: 32px;
    &:before, &:after {
      position: fixed !important;
    }
  }
}
.other {
  margin-right: 12px;
  .avatar {
    img {
      border-radius: 50%;
    }
    &:after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      width: 0;
      height: 0;
      border: 5px solid white;
      border-left-color: transparent;
      border-bottom-color: transparent;
      z-index: -1;
    }
  }
}

.self {
  justify-content: flex-end;
  align-items: flex-end;
  margin-left: 4px;
  .chat-message-div, .system-message-div {
    order: 1;
    border-bottom-right-radius: 0; // weird shadow fix
  }
  .avatar {
    order: 2;
    &:after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      width: 0;
      height: 0;
      border: 5px solid white;
      border-right-color: transparent;
      border-top-color: transparent;
      box-shadow: 1px 1px 2px rgba(black, 0.2); // not quite perfect but close
      z-index: -1;
    }
  }
}

.chat-message {
  padding: 0.15rem 0.15rem 0.15rem 0.15rem;
  overflow: visible;
  display: flex;
  flex-shrink: 0; // Firefox explicitly requires this to be set for normal operation
  min-height: min-content;
}

.avatar {
  // could set height, but gonna bottom-align instead
  position: relative; // for triangle
  img {
    display: block; // triangle position
    border-radius: 50%;
  }
}

.chat-message-div {
  box-shadow: 0 1px 2px rgba(black, 0.2);
  background: $chat-light-msg-bg;
}
.system-message-div {
  color: $chat-system-notice-color !important;
  margin: 0 $chat-system-margin-lr;
  padding-top: 2px !important;
  padding-bottom: 2px !important;

  .system-notice-icon {
    display: flex;
    justify-content: center;
    margin-right: 4px;
    ::v-deep span {
      height: 24px;
      line-height: 24px;
    }
  }

  .text-content {
    display: flex;
    align-items: center;
    &::before, &::after {
      content: '';
      background-color: $chat-system-hr-color;
      height: 1px;
      flex: 1;
    }
    &::before {
      right: 100%;
      margin-right: $chat-system-margin-lr;
    }
    &::after {
      left: 100%;
      margin-left: $chat-system-margin-lr;
    }
  }
  .text-content {
    display: flex;
    text-align: center;
    justify-content: flex-end;
  }
  .system-notice-icon {
    font-size: 1.2em !important;
    cursor: default;
    color: $chat-system-notice-icon-color;
  }

  .text {
    max-width: 85%;
  }

  ::v-deep strong {
    color: inherit;
  }
}

.system-message.tooltip.is-tooltip-top:hover {
  &::before, &::after {
    position: fixed !important;
  }
  &::after {
    border-color: rgba(32, 32, 32, 0.95) transparent transparent transparent !important;
    margin-top: -0.52em !important;
  }
  &::before {
    background: rgba(32, 32, 32, 0.95) !important;
    transform: translate(-80%, -2.12rem) !important;
  }
  .dark &:hover, .theater-mode &:hover {
    &::after {
      border-color: rgba(32, 32, 32, 0.95) transparent transparent transparent !important;
    }
    &::before {
      background: rgba(32, 32, 32, 0.95) !important;
    }
  }
}

.chat-message-div, .system-message-div {
  padding: 5px;
  border-radius: 2px;
  min-width: 30%;
  max-width: 100%;
  color: $chat-light-text;

  p {
    display: block;
    font-size: 0.91rem;
    margin: 0;
  }
  .text-content {
    // white-space: pre-wrap; /* Treat whitespace as you would in a textarea */
    word-break: break-word;
    ::v-deep strong {
      color: inherit;
    }
    ::v-deep .extra {
      font-size: 0.7em;
      color: #4169e1;
      .dark &, .theater-mode & {
        color: #79cbce;
      }
    }
  }
  time {
    font-size: 0.7rem;
    color: darken($chat-light-text, 15);
    &.tooltip.is-tooltip-top:hover {
      &::before, &::after {
        position: absolute !important;
      }
      &::after {
        border-color: rgba(32, 32, 32, 0.8) transparent transparent transparent !important;
        margin-top: -0.8em !important;
      }
      &::before {
        background: rgba(32, 32, 32, 0.8) !important;
        transform: translate(-80%, -2.34rem) !important;
      }
    }
  }

  .chat-message-entry-root, .from {
    margin-left: -2px;
    padding-left: 7px;
  }
  &.patron {
    .chat-message-entry-root, .from {
      padding-top: 2px;
    }
  }
  .from {
    position: relative;
    font-size: 0.8rem;
    font-weight: bold;
  }
}

@keyframes pulse {
  from { opacity: 0; }
  to { opacity: 0.5; }
}
</style>
